import React, { Fragment, useCallback, useEffect, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDebouncedCallback } from "use-debounce";
import Select from "react-select";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
// ArrowDownwardIcon removed — using single ArrowUpwardIcon with CSS rotation
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import GridViewIcon from "@mui/icons-material/GridView";
import ai from "../../helpers/axios";
import * as Sentry from "@sentry/react";

import MatchPairCard from "./match-pair-card";
import PFPSubscriptions from "./pfp-subscriptions";
import "./match-pair-card.css";

const sourceTypeOptions = [
  { label: "Lead", value: "lead" },
  { label: "Quote", value: "quote" },
  { label: "Contract", value: "contract" },
  { label: "ATC", value: "atc" },
];

const matchTypeOptions = [
  { label: "Complete Match", value: "complete" },
  { label: "Fragment (FOM)", value: "fom" },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "search":
      return { ...state, query: action.query };
    case "dataBeganDownloading":
      return { ...state, loading: action.payload.loadingStatus };
    case "dataDownloaded":
      return {
        ...state,
        matchPairs: action.payload.data,
        loading: false,
        totalCount: action.payload.count,
        hotCount: action.payload.hotCount ?? state.hotCount,
      };
    case "pageChanged":
      return { ...state, currentPage: action.payload.page };
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    default:
      return state;
  }
};

const PFPTestButtons = ({ getAccessTokenSilently, loginWithRedirect, onComplete }) => {
  const [status, setStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const busy = seeding || cleaning;

  const getToken = async () => {
    return getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
  };

  const handleSeed = async () => {
    setSeeding(true);
    setStatus(null);
    try {
      const token = await getToken();
      const response = await ai.auth(token).post("/api/tests/pfp/seed");
      const d = response?.data?.data || {};
      setStatus({
        success: true,
        message: `Seeded: ${d.leadsCreated || 0} leads, ${d.legsCreated || 0} legs, ${d.matchesFound || 0} matches`,
      });
      if (onComplete) onComplete();
    } catch (err) {
      console.error("PFP seed error:", err);
      setStatus({ success: false, message: err?.response?.data?.message || "Seed failed" });
    } finally {
      setSeeding(false);
    }
  };

  const handleCleanup = async () => {
    setCleaning(true);
    setStatus(null);
    try {
      const token = await getToken();
      const response = await ai.auth(token).post("/api/tests/pfp/cleanup");
      const d = response?.data?.data || {};
      setStatus({
        success: true,
        message: `Cleaned: ${d.leadsDeleted || 0} leads, ${d.legsDeleted || 0} legs, ${d.estimatesDeleted || 0} estimates`,
      });
      if (onComplete) onComplete();
    } catch (err) {
      console.error("PFP cleanup error:", err);
      setStatus({ success: false, message: err?.response?.data?.message || "Cleanup failed" });
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="pfp-test-tools">
      <button onClick={handleSeed} disabled={busy} className="pfp-test-tools__btn">
        {seeding && <span className="pfp-test-tools__spinner" />}
        {seeding ? "Seeding..." : "PFP Seed"}
      </button>
      <button onClick={handleCleanup} disabled={busy} className="pfp-test-tools__btn">
        {cleaning && <span className="pfp-test-tools__spinner" />}
        {cleaning ? "Cleaning..." : "PFP Cleanup"}
      </button>
      {status && (
        <div className={`pfp-status-banner pfp-status-banner--${status.success ? "success" : "error"}`}>
          {status.message}
        </div>
      )}
    </div>
  );
};

const FlightLegsContent = (props) => {
  const initialState = {
    matchPairs: [],
    loading: true,
    countPerPage: 25,
    currentPage: 1,
    totalCount: 0,
    hotCount: 0,
    query: "",
    sortBy: "priority",
    sortDirection: "asc",
    gridColumns: 1,
    sourceTypeFilter: [],
    sourceTypeFilterInput: null,
    matchTypeFilter: "",
    matchTypeFilterInput: null,
    priorityFilter: "",
    ownerFilter: [],
    ownerFilterInput: null,
    ownerOptions: [],
  };


  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const currentUserId = user?.sub || "";
  const userRoles = user && user["https://new.evojets.com/roles"];
  const isSuperAdmin =
    userRoles &&
    userRoles.some(
      (role) => role === "Super Admin" || role === "Account Director"
    );

  const [state, dispatch] = useReducer(reducer, initialState);
  const [showTestTools, setShowTestTools] = useState(false);
  const [listKey, setListKey] = useState(0);

  // Ctrl+. toggles test tools (no browser conflicts)
  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey && !e.shiftKey && !e.metaKey && e.key === ".") {
      e.preventDefault();
      setShowTestTools((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const getData = async (overrides = {}) => {
    const {
      query = state.query,
      loadSilently = false,
      page = state.currentPage,
      sourceTypeFilter = state.sourceTypeFilter,
      matchTypeFilter = state.matchTypeFilter,
      priorityFilter = state.priorityFilter,
      ownerFilter = state.ownerFilter,
      sortDirection = state.sortDirection,
    } = overrides;

    let loadingStatus = !loadSilently;
    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const params = {
      query,
      page,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection,
    };

    if (sourceTypeFilter.length) {
      params.sourceType = sourceTypeFilter.join(",");
    }
    if (matchTypeFilter) {
      params.matchType = matchTypeFilter;
    }
    if (priorityFilter === "hot") {
      params.maxPriority = 10;
    }
    if (ownerFilter.length) {
      params.assigneeIds = JSON.stringify(ownerFilter);
    }

    let response = await ai
      .auth(token)
      .get(`/api/flightLegs/matchPairs`, { params })
      .catch((error) => {
        console.error(error?.response?.data || error);
        Sentry.captureException(error);
      });

    if (response) {
      const pairs = response.data.data;
      dispatch({
        type: "dataDownloaded",
        payload: {
          data: pairs,
          count: response.data.count,
          hotCount: response.data.hotCount ?? pairs.filter((p) => p.priority <= 10).length,
        },
      });
    }
  };

  const debouncedGetData = useDebouncedCallback((query) => {
    getData({ query, loadSilently: true });
  }, 500);

  // Initial load + page/filter changes
  useEffect(() => {
    getData();
    document.title = `${
      props.pageTitle ? props.pageTitle : "Flight Matches"
    } | evoJets HQ`;
  }, [
    state.currentPage,
    state.countPerPage,
    state.sourceTypeFilter,
    state.matchTypeFilter,
    state.priorityFilter,
    state.ownerFilter,
    state.sortBy,
    state.sortDirection,
  ]);

  // Load owner options for super admins
  useEffect(() => {
    if (isSuperAdmin) {
      loadOwnerOptions();
    }
  }, [isSuperAdmin]);

  const loadOwnerOptions = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai.auth(token).get(`/api/users/roles/sales`, {});
    if (data.users.length >= 1) {
      let results = data.users.map((u) => ({
        value: u.user_id,
        label: u.name,
      }));
      dispatch({ type: "fieldChanged", field: "ownerOptions", value: results });
    }
  };

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });
    debouncedGetData(query);
  };

  const handleSourceTypeChange = (selected) => {
    const values = selected ? selected.map(({ value }) => value) : [];
    dispatch({
      type: "fieldChanged",
      field: "sourceTypeFilter",
      value: values,
    });
    dispatch({
      type: "fieldChanged",
      field: "sourceTypeFilterInput",
      value: selected,
    });
    dispatch({ type: "pageChanged", payload: { page: 1 } });
  };

  const handleMatchTypeChange = (selected) => {
    const value = selected ? selected.value : "";
    dispatch({
      type: "fieldChanged",
      field: "matchTypeFilter",
      value,
    });
    dispatch({
      type: "fieldChanged",
      field: "matchTypeFilterInput",
      value: selected,
    });
    dispatch({ type: "pageChanged", payload: { page: 1 } });
  };

  const handlePriorityFilter = (value) => {
    dispatch({
      type: "fieldChanged",
      field: "priorityFilter",
      value,
    });
    dispatch({ type: "pageChanged", payload: { page: 1 } });
  };

  const handleOwnerChange = (selected) => {
    const values = selected ? selected.map(({ value }) => value) : [];
    dispatch({
      type: "fieldChanged",
      field: "ownerFilter",
      value: values,
    });
    dispatch({
      type: "fieldChanged",
      field: "ownerFilterInput",
      value: selected,
    });
    dispatch({ type: "pageChanged", payload: { page: 1 } });
  };

  const handleSortToggle = () => {
    const newDirection = state.sortDirection === "asc" ? "desc" : "asc";
    dispatch({
      type: "fieldChanged",
      field: "sortDirection",
      value: newDirection,
    });
  };

  const handleSortKeyChange = (key) => {
    dispatch({ type: "fieldChanged", field: "sortBy", value: key });
  };

  const handleGridToggle = () => {
    dispatch({
      type: "fieldChanged",
      field: "gridColumns",
      value: state.gridColumns === 1 ? 2 : 1,
    });
    setListKey((k) => k + 1);
  };

  const handlePageChange = (newPage) => {
    dispatch({ type: "pageChanged", payload: { page: newPage } });
  };

  const totalPages = Math.ceil(state.totalCount / state.countPerPage);
  const startItem = (state.currentPage - 1) * state.countPerPage + 1;
  const endItem = Math.min(
    state.currentPage * state.countPerPage,
    state.totalCount
  );

  return (
    <div className="match-pairs-page">
      <div className="match-pairs__header">
        <div className="match-pairs__header-row">
          <div>
            <div className="match-pairs__header-pretitle">Business</div>
            <h1 className="match-pairs__header-title">
              {props.pageTitle || "Flight Matches"}
            </h1>
          </div>
          <div className="match-pairs__header-stats">
            <div className="match-pairs__header-stat">
              <div className="match-pairs__header-stat-value">{state.totalCount}</div>
              <div className="match-pairs__header-stat-label">Total</div>
            </div>
            <div className="match-pairs__header-stat">
              <div className="match-pairs__header-stat-value">{state.hotCount}</div>
              <div className="match-pairs__header-stat-label">Hot</div>
            </div>
          </div>
        </div>

        <div className="match-pairs__filter-bar">
          <input
            type="text"
            className="match-pairs__search-input"
            placeholder="Search by airport name or code"
            value={state.query}
            onChange={handleSearch}
          />
          <div className="match-pairs__filter-select">
            <Select
              options={sourceTypeOptions}
              placeholder="Source Type"
              onChange={handleSourceTypeChange}
              value={state.sourceTypeFilterInput}
              isMulti
              isClearable
            />
          </div>

          <div className="match-pairs__filter-select">
            <Select
              options={matchTypeOptions}
              placeholder="Match Type"
              onChange={handleMatchTypeChange}
              value={state.matchTypeFilterInput}
              isClearable
            />
          </div>

          <div className="match-pairs__filter-pills">
            <button
              className={`match-pairs__filter-pill${
                state.priorityFilter === "" ? " match-pairs__filter-pill--active" : ""
              }`}
              onClick={() => handlePriorityFilter("")}
            >
              All
            </button>
            <button
              className={`match-pairs__filter-pill${
                state.priorityFilter === "hot"
                  ? " match-pairs__filter-pill--active"
                  : ""
              }`}
              onClick={() => handlePriorityFilter("hot")}
            >
              Hot (1-10)
            </button>
          </div>

          {isSuperAdmin && (
            <div className="match-pairs__filter-select">
              <Select
                options={state.ownerOptions}
                placeholder="Owner"
                onChange={handleOwnerChange}
                value={state.ownerFilterInput}
                isMulti
                isClearable
              />
            </div>
          )}

          <div className="match-pairs__toolbar">
            <div className="match-pairs__sort-key">
              <button
                className={`match-pairs__sort-key-btn${
                  state.sortBy === "priority" ? " match-pairs__sort-key-btn--active" : ""
                }`}
                onClick={() => handleSortKeyChange("priority")}
                title="Sort by total score"
              >
                Total
              </button>
              <button
                className={`match-pairs__sort-key-btn${
                  state.sortBy === "corePriority" ? " match-pairs__sort-key-btn--active" : ""
                }`}
                onClick={() => handleSortKeyChange("corePriority")}
                title="Sort by Core Factors score"
              >
                Core
              </button>
            </div>
            <button
              className={`match-pairs__sort-toggle${state.sortDirection === "desc" ? " match-pairs__sort-toggle--desc" : ""}`}
              onClick={handleSortToggle}
              title={`Sort ${state.sortBy === "corePriority" ? "Core" : "total"} ${
                state.sortDirection === "asc" ? "ascending" : "descending"
              }`}
            >
              <ArrowUpwardIcon style={{ fontSize: 20 }} />
            </button>
            <button
              className="match-pairs__grid-toggle"
              onClick={handleGridToggle}
              title={state.gridColumns === 1 ? "Switch to grid view" : "Switch to list view"}
            >
              {state.gridColumns === 1 ? (
                <GridViewIcon style={{ fontSize: 20 }} />
              ) : (
                <ViewStreamIcon style={{ fontSize: 20 }} />
              )}
            </button>
          </div>

          <Link
            to="/archived-matches"
            className="match-pairs__archived-link"
          >
            View Archived Matches
          </Link>

          <PFPSubscriptions />
        </div>
      </div>

      {isSuperAdmin && showTestTools && (
        <div className="pfp-tools">
          <PFPTestButtons getAccessTokenSilently={getAccessTokenSilently} loginWithRedirect={loginWithRedirect} onComplete={() => getData({ loadSilently: true })} />
        </div>
      )}

      {state.loading && (
        <div className="match-pair-card__loader">
          <div className="match-pair-card__loader-spinner" />
          Loading match pairs...
        </div>
      )}

      {!state.loading && state.matchPairs.length === 0 && (
        <div className="match-pair-card__empty">
          No match pairs found.
        </div>
      )}

      {!state.loading && state.matchPairs.length > 0 && (
        <div
          key={listKey}
          className={`match-pairs__list${
            state.gridColumns === 2 ? " match-pairs__list--two-col" : ""
          }`}
        >
          {state.matchPairs.map((pair, index) => (
            <MatchPairCard
              key={pair.matchId}
              pair={pair}
              currentUserId={currentUserId}
              index={index}
              isSuperAdmin={isSuperAdmin}
              onArchived={() => getData({ loadSilently: true })}
            />
          ))}
        </div>
      )}

      {!state.loading && state.totalCount > 0 && (
        <div className="match-pairs__pagination">
          <div className="match-pairs__pagination-info">
            Showing {startItem}–{endItem} of {state.totalCount} pairs
          </div>
          <div className="match-pairs__pagination-buttons">
            <button
              disabled={state.currentPage <= 1}
              onClick={() => handlePageChange(state.currentPage - 1)}
            >
              Previous
            </button>
            <button
              disabled={state.currentPage >= totalPages}
              onClick={() => handlePageChange(state.currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightLegsContent;
